import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { VALIDATION_PATTERNS } from "../../constants/app";
import { sendForgotOtpReq, resetPasswordService } from "../../service/Auth.services";
import ShowToast from "components/ShowToast";
import { extractErrorMessage } from "utils/errorUtils";
import APP_CONFIG from "../../constants/app";

/* ─────────────────────────────────────────────
   Scoped styles — Fintech Animated Theme
   (Matches SignIn.jsx ft-* design system)
───────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .ft-page *, .ft-page *::before, .ft-page *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .ft-page {
    min-height: 100vh;
    display: flex;
    font-family: 'DM Sans', sans-serif;
    background: #060b18;
    overflow: hidden;
    position: relative;
  }

  /* ── Canvas ── */
  .ft-canvas {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  /* ── Particles ── */
  .ft-particles {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
  }
  .ft-particle {
    position: absolute;
    border-radius: 50%;
    opacity: 0;
    animation: ft-particle-float linear infinite;
  }
  @keyframes ft-particle-float {
    0%   { opacity: 0;   transform: translateY(100vh) scale(0); }
    10%  { opacity: 0.6; }
    90%  { opacity: 0.3; }
    100% { opacity: 0;   transform: translateY(-10vh) scale(1); }
  }

  /* ── Grid ── */
  .ft-grid {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    background-image:
      linear-gradient(rgba(0,212,170,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,212,170,0.04) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  /* ── Orbs ── */
  .ft-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 1;
    animation: ft-orb-drift ease-in-out infinite alternate;
  }
  .ft-orb-1 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(0,212,170,0.12) 0%, transparent 70%);
    top: -100px; left: -100px;
    animation-duration: 10s;
  }
  .ft-orb-2 {
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%);
    bottom: -80px; right: 300px;
    animation-duration: 14s;
    animation-delay: -3s;
  }
  .ft-orb-3 {
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%);
    top: 40%; right: -60px;
    animation-duration: 9s;
    animation-delay: -6s;
  }
  @keyframes ft-orb-drift {
    from { transform: translate(0, 0) scale(1); }
    to   { transform: translate(20px, 30px) scale(1.08); }
  }

  /* ── Ticker ── */
  .ft-ticker-wrap {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 2;
    height: 32px;
    background: rgba(0,212,170,0.05);
    border-top: 1px solid rgba(0,212,170,0.12);
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .ft-ticker {
    display: flex;
    gap: 0;
    animation: ft-ticker-scroll 28s linear infinite;
    white-space: nowrap;
  }
  @keyframes ft-ticker-scroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  .ft-ticker-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 28px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: rgba(0,212,170,0.55);
    letter-spacing: 0.04em;
    border-right: 1px solid rgba(0,212,170,0.1);
  }
  .ft-ticker-item .up   { color: #00d4aa; }
  .ft-ticker-item .down { color: #f43f5e; }

  /* ── Left panel ── */
  .ft-left {
    flex: 1;
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 60px 48px;
    animation: ft-fade-in 0.8s ease both;
  }
  @keyframes ft-fade-in {
    from { opacity: 0; transform: translateX(-24px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 64px;
    animation: ft-drop 0.6s ease both;
  }
  .ft-logo img { max-height: 36px; object-fit: contain; }

  .ft-headline {
    margin-bottom: 24px;
    animation: ft-drop 0.6s 0.1s ease both;
  }
  .ft-headline-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: #00d4aa;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 16px;
    padding: 4px 10px;
    border: 1px solid rgba(0,212,170,0.3);
    border-radius: 4px;
    background: rgba(0,212,170,0.06);
  }
  .ft-headline-tag::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #00d4aa;
    box-shadow: 0 0 8px #00d4aa;
    animation: ft-blink 1.8s ease-in-out infinite;
  }
  @keyframes ft-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
  .ft-headline h1 {
    font-size: clamp(2rem, 3.5vw, 2.8rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.12;
    letter-spacing: -0.02em;
  }
  .ft-headline h1 .accent { color: #00d4aa; }

  .ft-desc {
    font-size: 14px;
    color: rgba(255,255,255,0.4);
    line-height: 1.7;
    max-width: 380px;
    margin-bottom: 48px;
    animation: ft-drop 0.6s 0.2s ease both;
  }

  /* ── Steps indicator ── */
  .ft-steps {
    display: flex;
    align-items: center;
    gap: 0;
    animation: ft-drop 0.6s 0.3s ease both;
    margin-bottom: 40px;
  }
  .ft-step-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .ft-step-circle {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 1px solid rgba(0,212,170,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    font-weight: 500;
    color: rgba(0,212,170,0.5);
    background: rgba(0,212,170,0.04);
    transition: all 0.4s ease;
  }
  .ft-step-circle.active {
    border-color: #00d4aa;
    background: rgba(0,212,170,0.15);
    color: #00d4aa;
    box-shadow: 0 0 16px rgba(0,212,170,0.2);
  }
  .ft-step-circle.done {
    border-color: #00d4aa;
    background: #00d4aa;
    color: #020c08;
  }
  .ft-step-label {
    font-size: 11px;
    color: rgba(255,255,255,0.35);
    letter-spacing: 0.04em;
    transition: color 0.4s;
  }
  .ft-step-label.active { color: #00d4aa; }
  .ft-step-connector {
    width: 40px; height: 1px;
    background: rgba(0,212,170,0.15);
    margin: 0 8px;
  }
  .ft-step-connector.done { background: #00d4aa; }

  /* ── Security card ── */
  .ft-security-card {
    border: 1px solid rgba(0,212,170,0.15);
    border-radius: 12px;
    background: rgba(0,212,170,0.04);
    padding: 20px 24px;
    max-width: 320px;
    animation: ft-drop 0.6s 0.4s ease both;
  }
  .ft-security-card-title {
    font-size: 11px;
    font-weight: 600;
    color: #00d4aa;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .ft-security-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .ft-security-item:last-child { border-bottom: none; }
  .ft-security-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #00d4aa;
    flex-shrink: 0;
  }
  .ft-security-text {
    font-size: 12px;
    color: rgba(255,255,255,0.4);
  }

  /* ── Right panel ── */
  .ft-right {
    width: 460px;
    flex-shrink: 0;
    position: relative;
    z-index: 3;
    background: rgba(10,17,30,0.92);
    border-left: 1px solid rgba(255,255,255,0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 44px;
    animation: ft-slide-right 0.6s ease both;
    backdrop-filter: blur(20px);
  }
  @keyframes ft-slide-right {
    from { opacity: 0; transform: translateX(30px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-form-inner { width: 100%; }

  .ft-form-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 36px;
  }
  .ft-form-brand img { max-height: 32px; object-fit: contain; }

  .ft-form-hd { margin-bottom: 32px; }
  .ft-form-pre {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    color: #00d4aa;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ft-form-pre::before {
    content: '';
    display: block;
    width: 24px; height: 1px;
    background: #00d4aa;
  }
  .ft-form-title {
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 6px;
    letter-spacing: -0.02em;
    transition: all 0.3s ease;
  }
  .ft-form-sub {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    font-weight: 400;
    line-height: 1.6;
  }

  /* ── Step progress bar ── */
  .ft-progress-bar {
    height: 2px;
    background: rgba(255,255,255,0.06);
    border-radius: 2px;
    margin-bottom: 28px;
    overflow: hidden;
  }
  .ft-progress-fill {
    height: 100%;
    background: #00d4aa;
    border-radius: 2px;
    transition: width 0.5s ease;
    box-shadow: 0 0 8px rgba(0,212,170,0.4);
  }

  /* ── Alert ── */
  .ft-alert {
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: ft-alert-in 0.3s ease both;
  }
  @keyframes ft-alert-in {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .ft-alert-danger {
    background: rgba(244,63,94,0.08);
    color: #fb7185;
    border: 1px solid rgba(244,63,94,0.25);
  }

  /* ── Fields ── */
  .ft-field { margin-bottom: 18px; }
  .ft-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,0.45);
    margin-bottom: 8px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .ft-input-wrap { position: relative; }
  .ft-input-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 15px;
    pointer-events: none;
    color: rgba(255,255,255,0.25);
    transition: color 0.2s;
  }
  .ft-input {
    width: 100%;
    padding: 12px 16px 12px 42px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: #fff;
    background: rgba(255,255,255,0.04);
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    outline: none;
    caret-color: #00d4aa;
  }
  .ft-input::placeholder { color: rgba(255,255,255,0.2); }
  .ft-input:focus {
    border-color: rgba(0,212,170,0.5);
    background: rgba(0,212,170,0.04);
    box-shadow: 0 0 0 3px rgba(0,212,170,0.1);
  }
  .ft-input-error {
    border-color: rgba(244,63,94,0.4) !important;
    box-shadow: 0 0 0 3px rgba(244,63,94,0.08) !important;
  }
  .ft-error-msg {
    font-size: 11px;
    color: #fb7185;
    font-weight: 500;
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 4px;
    animation: ft-alert-in 0.2s ease both;
  }

  /* ── OTP field special ── */
  .ft-otp-input {
    letter-spacing: 14px;
    font-size: 20px;
    font-family: 'DM Mono', monospace;
    text-align: center;
    padding-left: 18px;
    padding-right: 8px;
    background: rgba(0,212,170,0.04);
    border-color: rgba(0,212,170,0.2);
  }
  .ft-otp-input:focus {
    border-color: rgba(0,212,170,0.6);
    box-shadow: 0 0 0 3px rgba(0,212,170,0.12);
  }

  /* ── Submit button ── */
  .ft-btn {
    width: 100%;
    padding: 13px 24px;
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    background: #00d4aa;
    color: #020c08;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 24px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    transition: transform 0.15s, box-shadow 0.15s, background 0.2s;
    position: relative;
    overflow: hidden;
  }
  .ft-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  .ft-btn:hover:not(:disabled)::before { transform: translateX(100%); }
  .ft-btn:hover:not(:disabled) {
    background: #00e8bb;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,212,170,0.3);
  }
  .ft-btn:active:not(:disabled) { transform: translateY(0); box-shadow: none; }
  .ft-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Divider ── */
  .ft-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }
  .ft-divider-line { flex: 1; height: 1px; background: rgba(255,255,255,0.07); }
  .ft-divider-text {
    font-size: 10px;
    color: rgba(255,255,255,0.2);
    font-weight: 600;
    letter-spacing: 0.1em;
  }

  .ft-signup-row {
    text-align: center;
    font-size: 12px;
    color: rgba(255,255,255,0.3);
    font-weight: 500;
  }
  .ft-signup-row a,
  .ft-signup-row button {
    background: none;
    border: none;
    padding: 0;
    color: #00d4aa;
    font-weight: 700;
    text-decoration: none;
    transition: opacity 0.15s;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
  }
  .ft-signup-row a:hover,
  .ft-signup-row button:hover { opacity: 0.75; }
  .ft-signup-row button:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Secure badge ── */
  .ft-secure-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 24px;
    font-size: 10px;
    color: rgba(255,255,255,0.2);
    letter-spacing: 0.06em;
  }
  .ft-secure-badge span { font-family: 'DM Mono', monospace; }
  .ft-secure-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #00d4aa;
    box-shadow: 0 0 6px #00d4aa;
    animation: ft-blink 2.5s ease-in-out infinite;
  }

  @keyframes ft-drop {
    from { opacity: 0; transform: translateY(-14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Step transition ── */
  .ft-step-enter {
    animation: ft-step-in 0.35s ease both;
  }
  @keyframes ft-step-in {
    from { opacity: 0; transform: translateX(20px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  @media (max-width: 991px) {
    .ft-left { display: none; }
    .ft-right { width: 100%; border-left: none; }
  }
  @media (max-width: 480px) {
    .ft-right { padding: 36px 24px; }
  }
`;

/* ─────────────────────────────────────────────
   Animated canvas background
───────────────────────────────────────────── */
function AnimatedCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let nodes = [];
    const NODE_COUNT = 40;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    const initNodes = () => {
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.1,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,212,170,${n.opacity * 0.4})`;
        ctx.fill();
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,212,170,${0.07 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    resize(); initNodes(); draw();
    window.addEventListener("resize", () => { resize(); initNodes(); });
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="ft-canvas" />;
}

/* ─────────────────────────────────────────────
   Floating Particles
───────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  size: `${Math.random() * 4 + 2}px`,
  delay: `${Math.random() * 20}s`,
  duration: `${Math.random() * 15 + 12}s`,
  color: i % 3 === 0 ? "rgba(0,212,170,0.5)" : i % 3 === 1 ? "rgba(59,130,246,0.4)" : "rgba(139,92,246,0.4)",
}));

function Particles() {
  return (
    <div className="ft-particles">
      {PARTICLES.map((p) => (
        <div key={p.id} className="ft-particle" style={{ left: p.left, width: p.size, height: p.size, background: p.color, animationDelay: p.delay, animationDuration: p.duration }} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticker strip
───────────────────────────────────────────── */
const TICKERS = [
  { sym: "BTC/USD", val: "67,420.50", chg: "+2.34%", up: true },
  { sym: "ETH/USD", val: "3,841.20",  chg: "+1.12%", up: true },
  { sym: "USDT",    val: "1.0002",    chg: "0.00%",  up: true },
  { sym: "BNB/USD", val: "598.30",    chg: "-0.87%", up: false },
  { sym: "SOL/USD", val: "178.45",    chg: "+3.56%", up: true },
  { sym: "XRP/USD", val: "0.6312",    chg: "-1.23%", up: false },
  { sym: "ADA/USD", val: "0.4523",    chg: "+0.94%", up: true },
  { sym: "DOGE",    val: "0.1842",    chg: "-2.11%", up: false },
];

function Ticker() {
  const items = [...TICKERS, ...TICKERS];
  return (
    <div className="ft-ticker-wrap" aria-hidden="true">
      <div className="ft-ticker">
        {items.map((t, i) => (
          <span key={i} className="ft-ticker-item">
            {t.sym}&nbsp;<span>{t.val}</span>&nbsp;<span className={t.up ? "up" : "down"}>{t.chg}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const ForGotPassword = () => {
  const [step, setStep] = useState(1); // 1: Phone, 2: OTP + New Password
  const [formData, setFormData] = useState({
    email: "", // Phone
    otp: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Step 1: Send OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!formData.email || !VALIDATION_PATTERNS.PHONE.test(formData.email)) {
      setErrors({ email: "Please enter a valid phone number" });
      return;
    }
    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      const result = await sendForgotOtpReq(_formData);
      if (result?.status) {
        setStep(2);
        const msg = result.message || "OTP sent successfully to your registered number.";
        ShowToast(msg, "success");
      } else {
        const errorMsg = extractErrorMessage(result, "Failed to send OTP. Please check your phone number.");
        setErrors({ general: errorMsg });
        ShowToast(errorMsg, "danger");
      }
    } catch (error) {
      console.error("Forgot OTP error:", error);
      const errorMsg = extractErrorMessage(error, "Connection error. Please try again.");
      setErrors({ general: errorMsg });
      ShowToast(errorMsg, "danger");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Final Submit (Verify OTP & Reset Password)
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (formData.otp.length < 4) newErrors.otp = "Enter valid OTP";
    if (formData.password.length < 6) newErrors.password = "Min 6 characters required";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      _formData.append("password", formData.password);
      _formData.append("otp", formData.otp);
      const resetRes = await resetPasswordService(_formData);
      if (resetRes?.status) {
        const successMsg = resetRes.message || "Password reset successfully!";
        ShowToast(successMsg, "success");
        navigate("/login", { state: { message: successMsg } });
      } else {
        const errorMsg = extractErrorMessage(resetRes, "Password reset failed. Please try again.");
        ShowToast(errorMsg, "danger");
        setErrors({ general: errorMsg });
      }
    } catch (error) {
      console.error("Reset password error:", error);
      const errorMsg = extractErrorMessage(error, "An unexpected error occurred. Please try again.");
      ShowToast(errorMsg, "danger");
      setErrors({ general: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>

      <div className="ft-page">
        {/* Animated background */}
        <AnimatedCanvas />
        <Particles />
        <div className="ft-grid" aria-hidden="true" />
        <div className="ft-orb ft-orb-1" aria-hidden="true" />
        <div className="ft-orb ft-orb-2" aria-hidden="true" />
        <div className="ft-orb ft-orb-3" aria-hidden="true" />

        {/* ── Left panel ── */}
        <div className="ft-left">
          <div className="ft-logo">
            <img src={APP_CONFIG.ORGANISATION_LOGO} alt="Logo" />
          </div>

          <div className="ft-headline">
            <div className="ft-headline-tag">Secure Recovery</div>
            <h1>
              Recover your<br />
              <span className="accent">account</span><br />
              securely
            </h1>
          </div>

          <p className="ft-desc">
            Verify your identity with a one-time code and set a new password
            — your data stays protected throughout.
          </p>

          {/* Step indicator on left panel */}
          <div className="ft-steps">
            <div className="ft-step-item">
              <div className={`ft-step-circle ${step === 1 ? "active" : "done"}`}>
                {step > 1 ? "✓" : "01"}
              </div>
              <span className={`ft-step-label ${step === 1 ? "active" : ""}`}>Phone</span>
            </div>
            <div className={`ft-step-connector ${step > 1 ? "done" : ""}`} />
            <div className="ft-step-item">
              <div className={`ft-step-circle ${step === 2 ? "active" : ""}`}>02</div>
              <span className={`ft-step-label ${step === 2 ? "active" : ""}`}>Reset</span>
            </div>
          </div>

          <div className="ft-security-card">
            <div className="ft-security-card-title">🔐 Security Guarantee</div>
            <div className="ft-security-item">
              <div className="ft-security-dot" />
              <span className="ft-security-text">OTP expires in 10 minutes</span>
            </div>
            <div className="ft-security-item">
              <div className="ft-security-dot" />
              <span className="ft-security-text">256-bit encrypted transmission</span>
            </div>
            <div className="ft-security-item">
              <div className="ft-security-dot" />
              <span className="ft-security-text">Previous sessions invalidated</span>
            </div>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="ft-right">
          <div className="ft-form-inner">

            <div className="ft-form-brand">
              <img src={APP_CONFIG.ORGANISATION_LOGO} alt="Logo" />
            </div>

            <div className="ft-form-hd">
              <p className="ft-form-pre">Retailer Portal</p>
              <h2 className="ft-form-title">
                {step === 1 ? "Forgot Password?" : "Verify & Reset"}
              </h2>
              <p className="ft-form-sub">
                {step === 1
                  ? "Enter your phone number to receive a verification code."
                  : `Enter the 4-digit code sent to ${formData.email}`}
              </p>
            </div>

            {/* Step progress bar */}
            <div className="ft-progress-bar">
              <div className="ft-progress-fill" style={{ width: step === 1 ? "50%" : "100%" }} />
            </div>

            {/* General error */}
            {errors.general && (
              <div className="ft-alert ft-alert-danger">
                <span>⚠️</span>
                {errors.general}
              </div>
            )}

            <form onSubmit={step === 1 ? handleRequestOtp : handleResetSubmit} noValidate>
              {step === 1 ? (
                /* ── STEP 1: PHONE ── */
                <div className="ft-field ft-step-enter">
                  <label className="ft-label" htmlFor="email">Phone Number</label>
                  <div className="ft-input-wrap">
                    <span className="ft-input-icon">📞</span>
                    <input
                      className={`ft-input${errors.email ? " ft-input-error" : ""}`}
                      id="email"
                      name="email"
                      type="text"
                      placeholder="e.g. 9876543210"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  {errors.email && <p className="ft-error-msg"><span>⚠</span>{errors.email}</p>}
                </div>
              ) : (
                /* ── STEP 2: OTP + PASSWORDS ── */
                <div className="ft-step-enter">
                  <div className="ft-field">
                    <label className="ft-label" htmlFor="otp">Verification Code (OTP)</label>
                    <div className="ft-input-wrap">
                      <input
                        className={`ft-input ft-otp-input${errors.otp ? " ft-input-error" : ""}`}
                        id="otp"
                        name="otp"
                        type="text"
                        maxLength="4"
                        placeholder="0 0 0 0"
                        value={formData.otp}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </div>
                    {errors.otp && <p className="ft-error-msg"><span>⚠</span>{errors.otp}</p>}
                  </div>

                  <div className="ft-field">
                    <label className="ft-label" htmlFor="password">New Password</label>
                    <div className="ft-input-wrap">
                      <span className="ft-input-icon">🔒</span>
                      <input
                        className={`ft-input${errors.password ? " ft-input-error" : ""}`}
                        id="password"
                        name="password"
                        type="password"
                        placeholder="Create new password"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </div>
                    {errors.password && <p className="ft-error-msg"><span>⚠</span>{errors.password}</p>}
                  </div>

                  <div className="ft-field">
                    <label className="ft-label" htmlFor="confirmPassword">Confirm Password</label>
                    <div className="ft-input-wrap">
                      <span className="ft-input-icon">🔒</span>
                      <input
                        className={`ft-input${errors.confirmPassword ? " ft-input-error" : ""}`}
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        placeholder="Repeat new password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </div>
                    {errors.confirmPassword && <p className="ft-error-msg"><span>⚠</span>{errors.confirmPassword}</p>}
                  </div>
                </div>
              )}

              {/* Submit */}
              <button className="ft-btn" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                      style={{ width: "1rem", height: "1rem", borderWidth: "2px" }}
                    />
                    {step === 1 ? "Sending..." : "Resetting..."}
                  </>
                ) : (
                  <>{step === 1 ? "Send OTP →" : "Reset Password ✓"}</>
                )}
              </button>
            </form>

            <div className="ft-divider">
              <div className="ft-divider-line" />
              <span className="ft-divider-text">OR</span>
              <div className="ft-divider-line" />
            </div>

            <div className="ft-signup-row">
              {step === 2 ? (
                <button type="button" onClick={() => setStep(1)} disabled={loading}>
                  ← Back to Phone Change
                </button>
              ) : (
                <>
                  Remember your password?{" "}
                  <a href="/login">Sign in</a>
                </>
              )}
            </div>

            <div className="ft-secure-badge">
              <div className="ft-secure-dot" aria-hidden="true" />
              <span>256-BIT SSL · OTP SECURED · PCI DSS</span>
            </div>

          </div>
        </div>

        {/* Ticker */}
        <Ticker />
      </div>
    </>
  );
};

export default ForGotPassword;